import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { api, getApiErrorMessage } from '../lib/api';
import { useAuthStore } from '../store/index';
import { Task } from '../types';

interface DashboardIncidents {
  statusCounts: {
    open: number;
    in_progress: number;
    closed: number;
    problem: number;
  };
  byDomain: Array<{ domain_id: string | null; domain_name: string; count: number }>;
  timeline: Array<{ date: string; count: number }>;
}

const Reports: React.FC = () => {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const canSeeTasks = user && (user.role === 'superadmin' || user.role === 'manager');

  const [dashboard, setDashboard] = useState<DashboardIncidents | null>(null);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [dashboardError, setDashboardError] = useState<string | null>(null);

  const [tasks, setTasks] = useState<Task[]>([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [tasksError, setTasksError] = useState<string | null>(null);

  const fetchDashboard = useCallback(async () => {
    setDashboardLoading(true);
    setDashboardError(null);
    try {
      const res = await api.get('/incidents/dashboard');
      const data = (res.data as any)?.data as DashboardIncidents;
      if (data) setDashboard(data);
    } catch (err: any) {
      setDashboardError(getApiErrorMessage(err, 'Failed to load dashboard.'));
    } finally {
      setDashboardLoading(false);
    }
  }, []);

  const fetchTasks = useCallback(async () => {
    if (!canSeeTasks) return;
    setTasksLoading(true);
    setTasksError(null);
    try {
      const res = await api.get('/tasks/dashboard');
      const data = (res.data as any)?.data;
      if (Array.isArray(data)) setTasks(data);
    } catch (err: any) {
      setTasksError(getApiErrorMessage(err, 'Failed to load task dashboard.'));
    } finally {
      setTasksLoading(false);
    }
  }, [canSeeTasks]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  useEffect(() => {
    if (canSeeTasks) {
      fetchTasks();
    }
  }, [canSeeTasks, fetchTasks]);

  const taskCounts = tasks.reduce(
    (acc, t) => {
      acc[t.status] = (acc[t.status] || 0) + 1;
      return acc;
    },
    { todo: 0, claimed: 0, completed: 0 } as Record<string, number>
  );

  const statusCards = [
    { key: 'open', label: t('incidents.detail.status.open'), count: dashboard?.statusCounts.open ?? 0, color: 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-900/40' },
    { key: 'in_progress', label: t('incidents.detail.status.inProgress'), count: dashboard?.statusCounts.in_progress ?? 0, color: 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-900/40' },
    { key: 'closed', label: t('incidents.detail.status.closed'), count: dashboard?.statusCounts.closed ?? 0, color: 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-900/40' },
    { key: 'problem', label: t('incidents.detail.status.problem'), count: dashboard?.statusCounts.problem ?? 0, color: 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-900/40' },
  ];

  const taskStatusCards = [
    { key: 'todo', label: t('tasks.detail.status.todo'), count: taskCounts.todo, color: 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600' },
    { key: 'claimed', label: t('tasks.detail.status.claimed'), count: taskCounts.claimed, color: 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-900/40' },
    { key: 'completed', label: t('tasks.detail.status.completed'), count: taskCounts.completed, color: 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-900/40' },
  ];

  const domainData = dashboard?.byDomain ?? [];
  const timelineData = dashboard?.timeline ?? [];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">{t('common.reports')}</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400">{t('reports.subtitle')}</p>
      </div>

      {/* Incident Status Cards */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-3">{t('reports.incidentStatus')}</h2>

        {dashboardLoading && (
          <div className="flex items-center justify-center py-8">
            <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <span className="ml-3 text-gray-600 dark:text-gray-400">{t('reports.loading')}</span>
          </div>
        )}

        {dashboardError && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-900/40 text-yellow-800 dark:text-yellow-400 px-4 py-3 rounded-lg text-sm mb-4">{dashboardError}</div>
        )}

        {!dashboardLoading && !dashboardError && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {statusCards.map((card) => (
              <div key={card.key} className={`rounded-lg border p-4 ${card.color}`}>
                <div className="text-2xl font-bold">{card.count}</div>
                <div className="text-sm font-medium mt-1">{card.label}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* By Domain Bar Chart */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-3">{t('reports.byDomain')}</h2>

        {dashboardLoading && (
          <div className="flex items-center justify-center py-8">
            <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <span className="ml-3 text-gray-600 dark:text-gray-400">{t('reports.loading')}</span>
          </div>
        )}

        {!dashboardLoading && dashboardError && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-900/40 text-yellow-800 dark:text-yellow-400 px-4 py-3 rounded-lg text-sm">{dashboardError}</div>
        )}

        {!dashboardLoading && !dashboardError && domainData.length === 0 && (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">{t('reports.noData')}</div>
        )}

        {!dashboardLoading && !dashboardError && domainData.length > 0 && (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={domainData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="domain_name" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#3B82F6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Timeline Area Chart */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-3">{t('reports.last30Days')}</h2>

        {dashboardLoading && (
          <div className="flex items-center justify-center py-8">
            <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <span className="ml-3 text-gray-600 dark:text-gray-400">{t('reports.loading')}</span>
          </div>
        )}

        {!dashboardLoading && dashboardError && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-900/40 text-yellow-800 dark:text-yellow-400 px-4 py-3 rounded-lg text-sm">{dashboardError}</div>
        )}

        {!dashboardLoading && !dashboardError && timelineData.length === 0 && (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">{t('reports.noData')}</div>
        )}

        {!dashboardLoading && !dashboardError && timelineData.length > 0 && (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={timelineData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(val: string) => val.slice(5)} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
              <Tooltip labelFormatter={(val: string) => val} />
              <Area type="monotone" dataKey="count" stroke="#3B82F6" fill="#93C5FD" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Task Status — only for superadmin/manager */}
      {canSeeTasks && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-3">{t('reports.taskStatus')}</h2>

          {tasksLoading && (
            <div className="flex items-center justify-center py-8">
              <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <span className="ml-3 text-gray-600 dark:text-gray-400">{t('reports.loading')}</span>
            </div>
          )}

          {tasksError && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-900/40 text-yellow-800 dark:text-yellow-400 px-4 py-3 rounded-lg text-sm mb-4">{tasksError}</div>
          )}

          {!tasksLoading && !tasksError && (
            <div className="grid grid-cols-3 gap-4">
              {taskStatusCards.map((card) => (
                <div key={card.key} className={`rounded-lg border p-4 ${card.color}`}>
                  <div className="text-2xl font-bold">{card.count}</div>
                  <div className="text-sm font-medium mt-1">{card.label}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Reports;